import { useState, useEffect } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Bell,
  BrainCircuit,
  ChevronDown,
  ChevronUp,
  ChevronsRight,
  CircleDollarSign,
  Clock,
  ExternalLink,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Star,
  Store,
  Users,
  X,
  Zap,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// ─── Types ────────────────────────────────────────────────────────────────────
type AdminPulsePriority = "high" | "medium" | "low";
type AdminPulseCategory =
  | "Signups & Waitlist"
  | "Billing"
  | "Support & Leads"
  | "AI Usage"
  | "Vendor & Marketplace"
  | "Mercury Health"
  | "Impersonation & Audit";

interface AdminPulseItem {
  itemKey: string;
  category: AdminPulseCategory;
  title: string;
  detail: string;
  timestamp: string;
  priority: AdminPulsePriority;
  deepLinkPath: string;
}

interface AdminPulseBriefing {
  generatedAt: string;
  source: "gemini" | "fallback";
  summaryNarrative: string;
  highlights: string[];
  items: AdminPulseItem[];
  windowDays: 7;
}

// ─── Category metadata ────────────────────────────────────────────────────────
const CATEGORY_META: Record<AdminPulseCategory, { icon: React.ComponentType<{ className?: string }>; color: string }> = {
  "Signups & Waitlist": { icon: Users, color: "text-brand" },
  "Billing": { icon: CircleDollarSign, color: "text-green-600 dark:text-green-400" },
  "Support & Leads": { icon: Bell, color: "text-brand-accent" },
  "AI Usage": { icon: BrainCircuit, color: "text-purple-600 dark:text-purple-400" },
  "Vendor & Marketplace": { icon: Store, color: "text-teal-600 dark:text-teal-400" },
  "Mercury Health": { icon: Activity, color: "text-blue-600 dark:text-blue-400" },
  "Impersonation & Audit": { icon: ShieldAlert, color: "text-red-600 dark:text-red-400" },
};

const PRIORITY_META: Record<AdminPulsePriority, { label: string; color: string }> = {
  high: { label: "High", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  medium: { label: "Medium", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  low: { label: "Low", color: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" },
};

// ─── Hooks ───────────────────────────────────────────────────────────────────
function useAdminPulse() {
  return useQuery<AdminPulseBriefing>({
    queryKey: ["/api/admin/pulse/briefing"],
    staleTime: 5 * 60 * 1000,
  });
}

function useRefreshBriefing() {
  const queryClient = useQueryClient();
  return useMutation<AdminPulseBriefing>({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/pulse/briefing/refresh");
      return res.json() as Promise<AdminPulseBriefing>;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/admin/pulse/briefing"], data);
    },
  });
}

function useSnoozeItem() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silentSave: true },
    mutationFn: ({ itemKey, hours }: { itemKey: string; hours: number }) =>
      apiRequest("POST", "/api/admin/pulse/snooze", { itemKey, hours }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/pulse/briefing"] }),
  });
}

function useDismissItem() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silentSave: true },
    mutationFn: (itemKey: string) =>
      apiRequest("POST", "/api/admin/pulse/dismiss", { itemKey }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/pulse/briefing"] }),
  });
}

function useMarkViewed() {
  return useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("POST", "/api/admin/pulse/view"),
  });
}

// ─── Item card ───────────────────────────────────────────────────────────────
function PulseItemCard({ item }: { item: AdminPulseItem }) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const snooze = useSnoozeItem();
  const dismiss = useDismissItem();
  const meta = CATEGORY_META[item.category];
  const priorityMeta = PRIORITY_META[item.priority];
  const Icon = meta?.icon ?? Bell;

  function handleOpen() {
    navigate(item.deepLinkPath);
  }

  function handleSnooze(hours: number) {
    snooze.mutate(
      { itemKey: item.itemKey, hours },
      {
        onSuccess: () => toast({ description: `Snoozed for ${hours >= 24 ? `${hours / 24}d` : `${hours}h`}` }),
        onError: () => toast({ description: "Could not snooze item", variant: "destructive" }),
      },
    );
  }

  function handleDismiss() {
    dismiss.mutate(item.itemKey, {
      onSuccess: () => toast({ description: "Item dismissed for 30 days" }),
      onError: () => toast({ description: "Could not dismiss item", variant: "destructive" }),
    });
  }

  const relativeTime = (() => {
    const diff = Date.now() - new Date(item.timestamp).getTime();
    const hours = Math.floor(diff / 3_600_000);
    if (hours < 1) return "just now";
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  })();

  return (
    <Card
      data-testid={`admin-pulse-item-${item.itemKey}`}
      className="group relative border-border/60 hover:border-border transition-colors"
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`mt-0.5 shrink-0 ${meta?.color ?? "text-muted-foreground"}`}>
            <Icon className="h-4 w-4" />
          </div>

          {/* Body */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span data-testid={`admin-pulse-item-title-${item.itemKey}`} className="font-medium text-sm leading-snug text-foreground">
                {item.title}
              </span>
              <Badge className={`text-[10px] px-1.5 py-0 h-4 font-medium border-0 ${priorityMeta.color}`}>
                {priorityMeta.label}
              </Badge>
            </div>
            {item.detail && (
              <p className="text-xs text-muted-foreground leading-relaxed mb-2">{item.detail}</p>
            )}
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>{relativeTime}</span>
              <span className="text-muted-foreground/40">·</span>
              <span>{item.category}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={handleOpen}
              data-testid={`admin-pulse-open-${item.itemKey}`}
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Open
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  data-testid={`admin-pulse-snooze-menu-${item.itemKey}`}
                  disabled={snooze.isPending || dismiss.isPending}
                >
                  <Clock className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleSnooze(4)} data-testid={`admin-pulse-snooze-4h-${item.itemKey}`}>
                  Snooze 4 hours
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSnooze(24)} data-testid={`admin-pulse-snooze-1d-${item.itemKey}`}>
                  Snooze 1 day
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSnooze(72)} data-testid={`admin-pulse-snooze-3d-${item.itemKey}`}>
                  Snooze 3 days
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSnooze(168)} data-testid={`admin-pulse-snooze-1w-${item.itemKey}`}>
                  Snooze 1 week
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={handleDismiss}
              data-testid={`admin-pulse-dismiss-${item.itemKey}`}
              disabled={dismiss.isPending}
              title="Dismiss for 30 days"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function PulseSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-32 w-full rounded-xl" />
      <div className="space-y-2">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-20 w-full rounded-lg" />
        ))}
      </div>
    </div>
  );
}

// ─── Summary block ────────────────────────────────────────────────────────────
function SummaryBlock({ briefing }: { briefing: AdminPulseBriefing }) {
  const [highlightsOpen, setHighlightsOpen] = useState(false);

  const sinceText = (() => {
    const d = new Date(briefing.generatedAt);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  })();

  return (
    <Card className="border-brand/20 bg-brand-muted/30" data-testid="admin-pulse-summary-card">
      <CardHeader className="pb-3 pt-4 px-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand shrink-0" />
            <CardTitle className="text-sm font-semibold text-foreground">
              Weekly Admin Briefing
            </CardTitle>
            {briefing.source === "gemini" ? (
              <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-brand/40 text-brand">
                AI
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-muted text-muted-foreground">
                Auto
              </Badge>
            )}
          </div>
          <span className="text-[11px] text-muted-foreground shrink-0">Updated {sinceText}</span>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-4 space-y-3">
        <p
          className="text-sm text-foreground/90 leading-relaxed"
          data-testid="admin-pulse-narrative"
        >
          {briefing.summaryNarrative}
        </p>

        {briefing.highlights.length > 0 && (
          <Collapsible open={highlightsOpen} onOpenChange={setHighlightsOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-0 text-xs text-brand hover:text-brand-hover hover:bg-transparent -ml-0.5"
                data-testid="admin-pulse-highlights-toggle"
              >
                {highlightsOpen ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
                {highlightsOpen ? "Hide" : "Show"} highlights
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul className="space-y-1.5 mt-2" data-testid="admin-pulse-highlights-list">
                {briefing.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Star className="h-3 w-3 mt-0.5 shrink-0 text-brand-accent" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Category section ─────────────────────────────────────────────────────────
function CategorySection({
  category,
  items,
}: {
  category: AdminPulseCategory;
  items: AdminPulseItem[];
}) {
  const [expanded, setExpanded] = useState(true);
  const meta = CATEGORY_META[category];
  const Icon = meta?.icon ?? Bell;
  const highCount = items.filter(i => i.priority === "high").length;

  return (
    <div data-testid={`admin-pulse-category-${category.replace(/\s+&?\s*/g, "-").toLowerCase()}`}>
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleTrigger asChild>
          <button
            className="flex items-center gap-2 w-full text-left py-1 group"
            data-testid={`admin-pulse-category-toggle-${category}`}
          >
            <Icon className={`h-3.5 w-3.5 shrink-0 ${meta?.color ?? "text-muted-foreground"}`} />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {category}
            </span>
            <span className="text-xs text-muted-foreground/60 ml-1">({items.length})</span>
            {highCount > 0 && (
              <Badge className="text-[10px] h-4 px-1.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-0 ml-1">
                {highCount} urgent
              </Badge>
            )}
            <span className="ml-auto text-muted-foreground/50 group-hover:text-muted-foreground">
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-2 mt-2">
            {items.map((item) => (
              <PulseItemCard key={item.itemKey} item={item} />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function EmptyState({ onRefresh }: { onRefresh: () => void }) {
  return (
    <div className="text-center py-16 text-muted-foreground" data-testid="admin-pulse-empty">
      <Zap className="h-8 w-8 mx-auto mb-3 opacity-40" />
      <p className="text-sm font-medium">All clear — no signals this week</p>
      <p className="text-xs mt-1 mb-4">Check back later or refresh to fetch the latest data.</p>
      <Button variant="outline" size="sm" onClick={onRefresh} data-testid="admin-pulse-empty-refresh">
        <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
        Refresh
      </Button>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function AdminPulse() {
  const [, navigate] = useLocation();
  const { toast } = useToast();

  // ── Admin auth gate (fail-closed) ────────────────────────────────────────
  const adminCheck = useQuery<{ authenticated: boolean; identity?: string | null }>({
    queryKey: ["/api/admin/check"],
    queryFn: async () => {
      const res = await fetch("/api/admin/check");
      return res.json();
    },
    retry: false,
    staleTime: 60_000,
  });

  const { data: briefing, isLoading, isError } = useAdminPulse();
  const refresh = useRefreshBriefing();
  const markViewed = useMarkViewed();

  // Mark viewed on mount (useEffect, not useState, to avoid StrictMode double-call)
  useEffect(() => {
    markViewed.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render loading spinner while auth check is in-flight
  if (adminCheck.isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Redirect to admin login if not authenticated — never render page shell
  if (!adminCheck.data?.authenticated) {
    navigate("/admin");
    return null;
  }

  function handleRefresh() {
    refresh.mutate(undefined, {
      onSuccess: () => toast({ description: "Briefing refreshed" }),
      onError: () => toast({ description: "Could not refresh briefing", variant: "destructive" }),
    });
  }

  // Group items by category
  const grouped = briefing
    ? (Object.keys(CATEGORY_META) as AdminPulseCategory[]).reduce<
        Record<AdminPulseCategory, AdminPulseItem[]>
      >(
        (acc, cat) => {
          acc[cat] = briefing.items.filter((i) => i.category === cat);
          return acc;
        },
        {} as Record<AdminPulseCategory, AdminPulseItem[]>,
      )
    : null;

  const activeCategories = grouped
    ? (Object.keys(CATEGORY_META) as AdminPulseCategory[]).filter(
        (cat) => (grouped[cat]?.length ?? 0) > 0,
      )
    : [];

  const highCount = briefing?.items.filter((i) => i.priority === "high").length ?? 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card px-6 py-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground"
                onClick={() => navigate("/admin")}
                data-testid="admin-pulse-back-btn"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1
                    className="text-lg font-semibold text-foreground"
                    data-testid="admin-pulse-title"
                  >
                    Admin Pulse
                  </h1>
                  {highCount > 0 && (
                    <Badge className="text-[10px] h-5 px-1.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-0">
                      <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                      {highCount} urgent
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  7-day platform briefing for super-admins
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin")}
                className="h-8 text-xs text-muted-foreground"
                data-testid="admin-pulse-admin-link"
              >
                <ChevronsRight className="h-3.5 w-3.5 mr-1" />
                Admin Panel
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={handleRefresh}
                disabled={refresh.isPending || isLoading}
                data-testid="admin-pulse-refresh-btn"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refresh.isPending ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-6 py-6 space-y-6">
        {isLoading && <PulseSkeleton />}

        {isError && !isLoading && (
          <Card className="border-destructive/30 bg-destructive/5" data-testid="admin-pulse-error">
            <CardContent className="p-5 text-center">
              <AlertTriangle className="h-6 w-6 text-destructive mx-auto mb-2" />
              <p className="text-sm font-medium text-destructive">Could not load briefing</p>
              <p className="text-xs text-muted-foreground mt-1 mb-3">
                Make sure you are logged in as a super-admin and try again.
              </p>
              <Button variant="outline" size="sm" onClick={handleRefresh} data-testid="admin-pulse-error-refresh">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Retry
              </Button>
            </CardContent>
          </Card>
        )}

        {briefing && !isLoading && (
          <>
            {/* AI Summary */}
            <SummaryBlock briefing={briefing} />

            {/* Feed */}
            {activeCategories.length === 0 ? (
              <EmptyState onRefresh={handleRefresh} />
            ) : (
              <div className="space-y-5" data-testid="admin-pulse-feed">
                {activeCategories.map((cat) => (
                  <CategorySection
                    key={cat}
                    category={cat}
                    items={grouped![cat]}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
